/**
 * EventDescriptionSectionParser
 *
 * Parses and serializes event descriptions with section delimiters.
 * Supports:
 * - Plain text (outside any section)
 * - Tasks section: ---tasks--- ... ---/tasks---
 * - Family section: ---family--- ... ---/family---
 *
 * This parser extracts sections and delegates task parsing to EventDescriptionParser.
 */

import { EventDescriptionParser, type ChecklistItem } from './EventDescriptionParser';
import type { EventParticipant } from '@/types/events';
import type { TransportAssignment } from '@/types/transport';

export type ParsedEventDescription = {
  plainText: string;
  tasks: ChecklistItem[];
  familyMemberIds: string[];
  participants: EventParticipant[];
  transport: TransportAssignment[];
  taskColor: string | null;
  meToggleOff: boolean;
};

class EventDescriptionSectionParserClass {
  // Section delimiter patterns
  private static readonly TASKS_SECTION_REGEX = /---tasks---([\s\S]*?)---\/tasks---/;
  private static readonly FAMILY_SECTION_REGEX = /---family---([\s\S]*?)---\/family---/;
  private static readonly PARTICIPANTS_SECTION_REGEX = /---participants---([\s\S]*?)---\/participants---/;
  private static readonly TRANSPORT_SECTION_REGEX = /---transport---([\s\S]*?)---\/transport---/;
  private static readonly TASKCOLOR_SECTION_REGEX = /---taskcolor---([\s\S]*?)---\/taskcolor---/;
  private static readonly METOGGLE_SECTION_REGEX = /---metoggle---([\s\S]*?)---\/metoggle---/;

  // Family member ID pattern (matches @family_xxx format)
  private static readonly FAMILY_ID_REGEX = /@(family_[a-z0-9_]+)/gi;

  /**
   * Parse a full event description into sections
   */
  parse(description: string | null | undefined): ParsedEventDescription {
    if (!description || description.trim() === '') {
      return { plainText: '', tasks: [], familyMemberIds: [], participants: [], transport: [], taskColor: null, meToggleOff: false };
    }

    let remainingText = description;
    let tasksContent = '';
    let familyContent = '';
    let participantsContent = '';
    let transportContent = '';

    // Extract tasks section
    const tasksMatch = description.match(
      EventDescriptionSectionParserClass.TASKS_SECTION_REGEX
    );
    if (tasksMatch) {
      tasksContent = tasksMatch[1];
      remainingText = remainingText.replace(tasksMatch[0], '');
    }

    // Extract family section
    const familyMatch = description.match(
      EventDescriptionSectionParserClass.FAMILY_SECTION_REGEX
    );
    if (familyMatch) {
      familyContent = familyMatch[1];
      remainingText = remainingText.replace(familyMatch[0], '');
    }

    // Extract participants section
    const participantsMatch = description.match(
      EventDescriptionSectionParserClass.PARTICIPANTS_SECTION_REGEX
    );
    if (participantsMatch) {
      participantsContent = participantsMatch[1];
      remainingText = remainingText.replace(participantsMatch[0], '');
    }

    // Extract transport section
    const transportMatch = description.match(
      EventDescriptionSectionParserClass.TRANSPORT_SECTION_REGEX
    );
    if (transportMatch) {
      transportContent = transportMatch[1];
      remainingText = remainingText.replace(transportMatch[0], '');
    }

    // Extract task color section
    let taskColor: string | null = null;
    const taskColorMatch = description.match(
      EventDescriptionSectionParserClass.TASKCOLOR_SECTION_REGEX
    );
    if (taskColorMatch) {
      taskColor = taskColorMatch[1].trim() || null;
      remainingText = remainingText.replace(taskColorMatch[0], '');
    }

    // Extract metoggle section
    let meToggleOff = false;
    const meToggleMatch = description.match(
      EventDescriptionSectionParserClass.METOGGLE_SECTION_REGEX
    );
    if (meToggleMatch) {
      meToggleOff = meToggleMatch[1].trim().toLowerCase() === 'off';
      remainingText = remainingText.replace(meToggleMatch[0], '');
    }

    // Parse tasks from tasks section content
    const tasks = this.parseTasksContent(tasksContent);

    // Parse family member IDs from family section content
    const familyMemberIds = this.parseFamilyContent(familyContent);

    // Parse participants from participants section content
    const participants = this.parseParticipantsContent(participantsContent);

    // Parse transport from transport section content
    const transport = this.parseTransportContent(transportContent);

    // Clean up remaining text (plain description)
    const plainText = remainingText.trim();

    return { plainText, tasks, familyMemberIds, participants, transport, taskColor, meToggleOff };
  }

  /**
   * Serialize sections back to a full description string
   */
  serialize(
    plainText: string,
    tasks: ChecklistItem[],
    familyMemberIds: string[],
    transport: TransportAssignment[] = [],
    taskColor: string | null = null,
    meToggleOff: boolean = false,
  ): string {
    const parts: string[] = [];

    // Add plain text if present
    if (plainText.trim()) {
      parts.push(plainText.trim());
    }

    // Add tasks section if there are tasks
    if (tasks.length > 0) {
      const tasksContent = this.serializeTasksContent(tasks);
      parts.push(`---tasks---\n${tasksContent}\n---/tasks---`);
    }

    // Always emit family section to mark event as explicitly labeled
    if (familyMemberIds.length > 0) {
      const familyContent = familyMemberIds.map(id => `@${id}`).join(' ');
      parts.push(`---family---\n${familyContent}\n---/family---`);
    } else {
      parts.push(`---family---\n---/family---`);
    }

    // Add transport section if there are transport assignments
    if (transport.length > 0) {
      const transportContent = this.serializeTransportContent(transport);
      parts.push(`---transport---\n${transportContent}\n---/transport---`);
    }

    // Add task color section if a color is set
    if (taskColor) {
      parts.push(`---taskcolor---\n${taskColor}\n---/taskcolor---`);
    }

    // Add metoggle section if Me is toggled off
    if (meToggleOff) {
      parts.push(`---metoggle---\nOff\n---/metoggle---`);
    }

    return parts.join('\n\n');
  }

  /**
   * Parse task checkboxes from tasks section content
   */
  private parseTasksContent(content: string): ChecklistItem[] {
    if (!content.trim()) {
      return [];
    }

    // Use EventDescriptionParser to parse the checkbox items
    const parsed = EventDescriptionParser.parse(content);
    return parsed.checklistItems;
  }

  /**
   * Serialize tasks to checkbox format
   */
  private serializeTasksContent(tasks: ChecklistItem[]): string {
    return tasks
      .map(task => {
        const checkbox = task.completed ? '[x]' : '[ ]';
        return `${checkbox} ${task.text}`;
      })
      .join('\n');
  }

  /**
   * Parse family member IDs from family section content
   */
  private parseFamilyContent(content: string): string[] {
    if (!content.trim()) {
      return [];
    }

    const ids: string[] = [];
    let match;

    // Reset regex lastIndex for global matching
    const regex = new RegExp(
      EventDescriptionSectionParserClass.FAMILY_ID_REGEX.source,
      'gi'
    );

    while ((match = regex.exec(content)) !== null) {
      ids.push(match[1]);
    }

    return ids;
  }

  /**
   * Parse participants from participants section content
   */
  private parseParticipantsContent(content: string): EventParticipant[] {
    if (!content.trim()) {
      return [];
    }

    return content
      .split('\n')
      .filter(line => line.trim())
      .map(line => {
        try {
          const data = JSON.parse(line);
          return {
            id: `${data.role}-${data.email || Date.now()}`,
            role: data.role,
            email: data.email,
            name: data.name ?? null,
            avatarUrl: data.avatarUrl ?? null,
            isSelf: data.isSelf ?? false,
          } as EventParticipant;
        } catch (error) {
          console.error('[EventDescriptionSectionParser] Failed to parse participant:', line, error);
          return null;
        }
      })
      .filter(p => p !== null) as EventParticipant[];
  }

    /**
   * Parse transport assignments from transport section content
   */
  private parseTransportContent(content: string): TransportAssignment[] {
    if (!content.trim()) {
      return [];
    }

    return content
      .split('\n')
      .filter(line => line.trim())
      .map(line => {
        try {
          const data = JSON.parse(line);
          return {
            id: data.id || `transport_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            mode: data.mode || 'car',
            vehicleName: data.vehicleName,
            driverFamilyMemberId: data.driverFamilyMemberId,
            passengerFamilyMemberIds: data.passengerFamilyMemberIds || [],
            notes: data.notes,
          } as TransportAssignment;
        } catch (error) {
          console.error('[EventDescriptionSectionParser] Failed to parse transport:', line, error);
          return null;
        }
      })
      .filter(t => t !== null) as TransportAssignment[];
  }

  /**
   * Serialize transport assignments to JSON lines format
   */
  private serializeTransportContent(transport: TransportAssignment[]): string {
    return transport
      .map(t =>
        JSON.stringify({
          id: t.id,
          mode: t.mode,
          vehicleName: t.vehicleName,
          driverFamilyMemberId: t.driverFamilyMemberId,
          passengerFamilyMemberIds: t.passengerFamilyMemberIds,
          notes: t.notes,
        })
      )
      .join('\n');
  }

  /**
   * Update only the family member IDs in a description
   * Preserves plain text, tasks, participants, and transport
   */
  updateFamilyMemberIds(
    description: string,
    newFamilyMemberIds: string[]
  ): string {
    const parsed = this.parse(description);
    return this.serialize(parsed.plainText, parsed.tasks, newFamilyMemberIds, parsed.transport, parsed.taskColor, parsed.meToggleOff);
  }

  /**
   * Check if a description has an explicit family section marker.
   * Used to distinguish legacy events (no marker → default to "Me")
   * from new events where an empty family section means "no labels".
   */
  hasFamilySection(description: string | null | undefined): boolean {
    if (!description) return false;
    return EventDescriptionSectionParserClass.FAMILY_SECTION_REGEX.test(description);
  }

  /**
   * Check if a description has any sections
   */
  hasSections(description: string | null | undefined): boolean {
    if (!description) return false;

    return (
      EventDescriptionSectionParserClass.TASKS_SECTION_REGEX.test(description) ||
      EventDescriptionSectionParserClass.FAMILY_SECTION_REGEX.test(description)
    );
  }

  /**
   * Quick check if the metoggle section marks Me as off.
   * Used by FamilyResourceMapper without full parsing overhead.
   */
  extractMeToggleOff(description: string | null | undefined): boolean {
    if (!description) return false;

    const match = description.match(
      EventDescriptionSectionParserClass.METOGGLE_SECTION_REGEX
    );
    if (!match) return false;

    return match[1].trim().toLowerCase() === 'off';
  }

  /**
   * Extract only family member IDs without full parsing
   * Useful for quick lookups in FamilyResourceMapper
   */
  extractFamilyMemberIds(description: string | null | undefined): string[] {
    if (!description) return [];

    const familyMatch = description.match(
      EventDescriptionSectionParserClass.FAMILY_SECTION_REGEX
    );

    if (!familyMatch) return [];

    return this.parseFamilyContent(familyMatch[1]);
  }

  /**
   * Quick-extract taskcolor without full parsing
   */
  extractTaskColor(description: string | null | undefined): string | null {
    if (!description) return null;
    const match = description.match(
      EventDescriptionSectionParserClass.TASKCOLOR_SECTION_REGEX
    );
    if (!match) return null;
    const value = match[1].trim();
    return value.length > 0 ? value : null;
  }
}

export const EventDescriptionSectionParser = new EventDescriptionSectionParserClass();
